/**
 * The four cards above the invoice table (R5).
 *
 * FOUR PROVIDER COLUMNS, NOT A PARTITION. Total, Bezahlt, Offen and Überfällig
 * come from R4 as four separate figures and none is derived from the others. On
 * the Bexio side a partially paid invoice contributes money to BOTH `paid` and
 * `open`, so the counts can overlap and `total` is not `paid` plus `open`. That
 * is the provider's reality rather than a defect — and it is exactly why there
 * is no ring, no stacked bar and no percentage here: every one of those shapes
 * asserts that the parts add up to the whole.
 *
 * NOTHING IS COMPUTED. This component receives the summary and renders it. It
 * never sees the rows below, so it cannot accidentally describe one page of them
 * instead of the population.
 *
 * A FAILED READ IS NOT ZERO. "CHF 0.00" is the most reassuring possible lie: a
 * calm, plausible figure that says the organisation is owed nothing. When the
 * summary did not arrive, each card says "Nicht verfügbar" and offers to try
 * again — `OverviewKpiCard` already renders exactly that. A genuinely empty
 * selection IS "CHF 0.00" and says so.
 *
 * The accents are the shared palette's semantic pairs (success, warning,
 * danger), never values lifted off the reference screenshot: a hard-coded hue is
 * a white square on the dark page.
 */
import React from "react";
import { AlertTriangle, CheckCircle2, Clock, Receipt } from "lucide-react";

import OverviewKpiCard from "../../../components/admin/companyOverview/OverviewKpiCard";
import { fmtMoney } from "../../../components/admin/bexio/bexioFormat";

/** Card order is reading order: what was billed, what came in, what has not. */
const CARDS = [
  { key: "total", title: "Total", accent: "blue", Icon: Receipt },
  { key: "paid", title: "Bezahlt", accent: "emerald", Icon: CheckCircle2 },
  { key: "open", title: "Offen", accent: "amber", Icon: Clock },
  { key: "overdue", title: "Überfällig", accent: "rose", Icon: AlertTriangle },
];

/** "40 Rechnungen", "1 Rechnung" — a count that reads as German, not as data. */
function invoiceCount(count) {
  if (typeof count !== "number") return undefined;
  return `${count} ${count === 1 ? "Rechnung" : "Rechnungen"}`;
}

export default function InvoiceKpiGrid({ summary, loading = false, error = "", onRetry }) {
  // An error outranks a payload that is still in hand: figures from an unknown
  // moment, shown as current ones, are worse than no figures.
  const buckets = error ? null : summary;

  return (
    <div
      data-testid="invoice-kpi-grid"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {CARDS.map(({ key, title, accent, Icon }) => {
        const bucket = buckets ? buckets[key] : null;
        return (
          <OverviewKpiCard
            key={key}
            testId={`invoice-kpi-${key}`}
            title={title}
            accent={accent}
            icon={<Icon className="h-4 w-4" />}
            // Four cards share the main column beside the sidebar, so the
            // large size wraps "CHF" onto its own line at 1440. Compact keeps
            // each figure on one line where a reader compares them.
            compact
            loading={loading}
            error={error}
            onRetry={onRetry}
            // The currency is the SUMMARY's, not the bucket's: R4 declares it
            // `"CHF"` and it names the COHORT that was summed rather than any
            // one invoice's denomination — foreign invoices are excluded from
            // these four figures and reported separately, never converted. That
            // is why `fmtMoney` is right here and `fmtInvoiceMoney`, which
            // exists for a nullable per-invoice code, is not.
            value={bucket ? fmtMoney(bucket.amount, summary.currency) : null}
            subtext={bucket ? invoiceCount(bucket.count) : undefined}
          />
        );
      })}
    </div>
  );
}
